import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMasterAdmin, setIsMasterAdmin] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalAdminToken, setOriginalAdminToken] = useState(null);
  const [adminData, setAdminData] = useState({ businesses: [], users: [] });

  const fetchUserPermissions = async (userData) => {
    try {
      const response = await axios.get(`${API}/me/permissions`);
      const userPermissions = response.data.permissions;
      setPermissions(userPermissions);
      localStorage.setItem('permissions', JSON.stringify(userPermissions));
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      const fullPermissions = {
        dashboard: { view: true },
        clients: { view: true, create: true, edit: true, delete: true },
        appointments: { view: true, view_all: true, create: true, edit: true, delete: true },
        listings: { view: true, edit: true, manage_media: true },
        team: { view: true, manage: true },
        settings: { view_products: true, manage_products: true, manage_business: true },
        tasks: { view: true, view_all: true, manage: true }
      };
      setPermissions(fullPermissions);
      localStorage.setItem('permissions', JSON.stringify(fullPermissions));
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const storedPermissions = localStorage.getItem('permissions');
    const savedOriginalToken = localStorage.getItem('originalAdminToken');
    const cachedIsMasterAdmin = localStorage.getItem('isMasterAdmin');
    
    if (token && userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      if (savedOriginalToken) {
        setOriginalAdminToken(savedOriginalToken);
        setIsImpersonating(true);
      }
      
      if (cachedIsMasterAdmin !== null) {
        setIsMasterAdmin(cachedIsMasterAdmin === 'true');
      } else {
        axios.get(`${API}/admin/check`).then(res => {
          setIsMasterAdmin(res.data.is_master_admin);
          localStorage.setItem('isMasterAdmin', res.data.is_master_admin.toString());
        }).catch(() => {
          setIsMasterAdmin(false);
          localStorage.setItem('isMasterAdmin', 'false');
        });
      }
      
      if (storedPermissions) {
        setPermissions(JSON.parse(storedPermissions));
      } else {
        fetchUserPermissions(parsedUser);
      }
    }
    setLoading(false);
  }, []);

  const login = async (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    try {
      const adminCheck = await axios.get(`${API}/admin/check`);
      setIsMasterAdmin(adminCheck.data.is_master_admin);
    } catch (e) {
      setIsMasterAdmin(false);
    }
    
    await fetchUserPermissions(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    localStorage.removeItem('originalAdminToken');
    localStorage.removeItem('isMasterAdmin');
    setUser(null);
    setPermissions(null);
    setIsMasterAdmin(false);
    setIsImpersonating(false);
    setOriginalAdminToken(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const hasPermission = (resource, action) => {
    if (!permissions) return false;
    const resourcePerms = permissions[resource];
    if (!resourcePerms) return false;
    return resourcePerms[action] === true;
  };

  return (
    <AuthContext.Provider value={{ user, permissions, hasPermission, login, logout, loading, isMasterAdmin, isImpersonating, setIsImpersonating, originalAdminToken, setOriginalAdminToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const AuthConsumer = ({ children }) => {
  const auth = useAuth();
  return children(auth);
};

export default AuthContext;
